import { CONTENT_TAG_CATEGORIES_MAP, CONTENT_TAG_FOCUS_MAP } from './constants';

/**
 * Returns the first focus found as a string from an array of content tags.
 * @param {Array} contentTags Array of content tags (with title, category and _id)
 */
export const getFocusFromContentTags = (contentTags) => {
  const focusTag = contentTags.find((tag) => tag.category === CONTENT_TAG_CATEGORIES_MAP.focus);
  return focusTag ? focusTag.title : null;
};

export const flattenContentTag = (tag) => {
  if (Array.isArray(tag.children)) {
    return [tag, ...tag.children.reduce((pv, child) => [...pv, ...flattenContentTag(child)], [])];
  }

  return [tag];
};

export const isFocus = (tagName) => Object.values(CONTENT_TAG_FOCUS_MAP).includes(tagName);

//===== START VERY CLASS-SPECIFIC TREE MANIPULATION METHODS =====/
/**
 * I put these set of methods here in the component because we are not certain
 * if we need to push these new specific logic into the shared tree component
 * If we find ourselves needing similar logic in the future, we might want to
 * incorporate these logics into NewTagsTreeSelector.
 *
 * There are a few needs here that caused us to build these methods:
 * 1. We need to filter out all nodes but curriculum and curriculum level
 * 2. We only allow leaf nodes to be selectable
 * 3. This is a single select tree (versus NewTagsTreeSelector has custom logic
 * that breaks for non-multiple usecase)
 * 4. We hide any subtrees that do not contain the kind of leaf nodes that we want
 *
 */

/**
 * node - the root node of the subtree
 * includeCategories - all the categories to be included in the tree
 * leafCategories - the known leaf categories for the subtree to preserve.
 * note that any subtree that's not a leaf node and does not have a child would be
 * purged
 */
const processSubtree = (node, includeCategories, leafCategories) => {
  if (!includeCategories.includes(node.category)) {
    return null;
  }

  if (node.children) {
    const newChildren = [];
    for (const child of node.children) {
      const subtree = processSubtree(child, includeCategories, leafCategories);
      if (subtree) {
        newChildren.push(subtree);
      }
    }

    node.children = newChildren;
  } else {
    // hide leaf nodes that's not one of the allowed leaf nodes categories
    if (!leafCategories.includes(node.category)) {
      return null;
    }
  }

  node.selectable = leafCategories.includes(node.category);
  return node;
};

const hasLeaf = (subtree, leafId) => {
  if (subtree._id === leafId) {
    return true;
  }

  if (subtree.children) {
    return subtree.children.some((child) => hasLeaf(child, leafId));
  }

  return false;
};

export const getClassContentTree = (contentTagsTree) => {
  const copyOfTree = JSON.parse(JSON.stringify(contentTagsTree));
  const processedContentTree = [];
  for (const rootNode of copyOfTree) {
    const newRoot = processSubtree(
      rootNode,
      [CONTENT_TAG_CATEGORIES_MAP.curriculum, CONTENT_TAG_CATEGORIES_MAP.curriculumLevel],
      [CONTENT_TAG_CATEGORIES_MAP.curriculumLevel]
    );

    if (newRoot) {
      processedContentTree.push(newRoot);
    }
  }

  return processedContentTree;
};

export const getSubthemeContentTree = (contentTagsTree, curriculumTagId = null) => {
  const copyOfTree = JSON.parse(JSON.stringify(contentTagsTree));
  const processedContentTree = [];
  for (const rootNode of copyOfTree) {
    const newRoot = processSubtree(
      rootNode,
      [
        CONTENT_TAG_CATEGORIES_MAP.curriculum,
        CONTENT_TAG_CATEGORIES_MAP.theme,
        CONTENT_TAG_CATEGORIES_MAP.subtheme,
        CONTENT_TAG_CATEGORIES_MAP.meeoppSubtopic,
        CONTENT_TAG_CATEGORIES_MAP.meeoppTopic,
      ],
      [CONTENT_TAG_CATEGORIES_MAP.subtheme, CONTENT_TAG_CATEGORIES_MAP.meeoppSubtopic]
    );

    if (!newRoot) {
      continue;
    }

    if (curriculumTagId && newRoot._id !== curriculumTagId) {
      continue;
    }

    processedContentTree.push(newRoot);
  }

  return processedContentTree;
};

export const extractBranchByLeafId = (contentTagsTree, selectedLeafId) => {
  const rootCurriculum = contentTagsTree.find((node) => hasLeaf(node, selectedLeafId));
  const findAndCopy = (node) => {
    // shallow copy is good enough here as we use DFS to copy the lowest level of the tree we need
    // anyway. So no need for deepcopy
    const copyNode = { ...node };
    if (copyNode._id === selectedLeafId) {
      copyNode.children = [];
      return copyNode;
    }

    if (copyNode.children) {
      for (const child of copyNode.children) {
        const subtree = findAndCopy(child);
        if (subtree) {
          copyNode.children = [subtree];
          return copyNode;
        }
      }
    }

    return null;
  };

  const myTree = findAndCopy(rootCurriculum);
  return myTree ? [myTree] : [];
};

//===== END VERY CLASS-SPECIFIC TREE MANIPULATION METHODS =====/
